import { useEffect, useState } from "react";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { BookmarkSimple, X } from "@phosphor-icons/react";
import { CATEGORY_LABELS, KINDS, cleanLine, saveToLibrary } from "./CaveatLibraryPicker";

/* Line under a right-click in a textarea (highlighted text wins). "" when there is nothing worth saving. */
export function lineAtContextMenu(e, value) {
  const el = e.currentTarget;
  const text = value || "";
  const start = el.selectionStart ?? 0;
  const end = el.selectionEnd ?? 0;
  if (end > start) return cleanLine(text.slice(start, end));
  const lineStart = text.lastIndexOf("\n", Math.max(0, start - 1)) + 1;
  const nl = text.indexOf("\n", start);
  return cleanLine(text.slice(lineStart, nl === -1 ? text.length : nl));
}

/* onContextMenu handler factory: opens the save menu for the clicked line, otherwise lets the browser menu through. */
export const saveLineHandler = (value, setMenu) => (e) => {
  const line = lineAtContextMenu(e, value);
  if (line.length < 4) return;
  e.preventDefault();
  setMenu({ x: e.clientX, y: e.clientY, line });
};

/* Floating "Save as a standard …" card. menu = {x, y, line} | null */
export default function SaveLineMenu({ kind = "caveat", menu, category = "general", onClose }) {
  const [cat, setCat] = useState(category || "general");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setCat(category || "general"); }, [menu, category]);
  useEffect(() => {
    if (!menu) return undefined;
    const close = (e) => { if (!e.target.closest?.("[data-save-line-menu], [role=listbox]")) onClose(); };
    const esc = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("mousedown", close); document.removeEventListener("keydown", esc); };
  }, [menu, onClose]);
  if (!menu) return null;
  const K = KINDS[kind] || KINDS.caveat;
  const left = Math.max(8, Math.min(menu.x, window.innerWidth - 340));
  const top = Math.max(8, Math.min(menu.y, window.innerHeight - 150));
  const save = async () => {
    setBusy(true);
    const ok = await saveToLibrary(kind, menu.line, cat);
    setBusy(false);
    if (ok) onClose();
  };
  return (
    <div data-save-line-menu className="fixed z-[100] w-[320px] rounded-lg border border-border bg-popover text-popover-foreground shadow-xl p-3 space-y-2" style={{ left, top }} data-testid={`${kind}-save-line-menu`}>
      <div className="flex items-start gap-2">
        <BookmarkSimple size={16} weight="fill" className="text-primary shrink-0 mt-0.5" />
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold">Save as a standard {K.noun}</p>
          <p className="text-[11px] text-muted-foreground line-clamp-3" data-testid={`${kind}-save-line-preview`}>{menu.line}</p>
        </div>
        <button type="button" onClick={onClose} className="text-muted-foreground hover:text-foreground" aria-label="Close" data-testid={`${kind}-save-line-close`}><X size={14} /></button>
      </div>
      <div className="flex items-center gap-2">
        <Select value={cat} onValueChange={setCat}>
          <SelectTrigger className="h-8 text-xs flex-1" data-testid={`${kind}-save-line-category`}><SelectValue /></SelectTrigger>
          <SelectContent>{Object.keys(CATEGORY_LABELS).map((c) => <SelectItem key={c} value={c}>{CATEGORY_LABELS[c]}</SelectItem>)}</SelectContent>
        </Select>
        <Button type="button" size="sm" className="h-8 text-xs" disabled={busy} onClick={save} data-testid={`${kind}-save-line-btn`}>
          {busy ? "Saving…" : "Save"}
        </Button>
      </div>
    </div>
  );
}
